import * as React from 'react';
import { Field, Formik, FormikProps } from 'formik';
import {
    CartItem,
    ShoppingCartContext,
    StorableDataKeys,
    VenuesShoppingCartModalList,
    WSAppContext,
} from '@weddingspot/ws-core-components';

import TextInput from '../input-text';
import inquiryValidator from './utils/InquiryValidator';
import DatePickerField from './form/FormikDatePicker';
import FormikCheckbox from './form/FormikCheckbox';
import InputTextarea from '../input-textarea/InputTextarea';
import { CheckoutContext, Steps } from './utils/CheckoutContext';
import { Appointments, Venues, VendorsApi } from '@weddingspot/ws-api-client';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import { format } from 'date-fns';

const styles = require('./CheckOutPage.module.scss');

export interface InquiryFormInterface {
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    guest_count: string;
    date: null | string;
    checkbox: boolean;
    message: string;
}

export const initialInquiry: InquiryFormInterface = {
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    guest_count: '',
    date: null,
    checkbox: false,
    message:
        'Hi,\n' + 'I found you on Wedding Spot and I would like to receive more information about having a wedding at your venue.\n' + '\n',
};

const InquiryForm = () => {
    const { user } = React.useContext(WSAppContext);

    const { items, removeItem, resetCart } = React.useContext(ShoppingCartContext);

    const { setConfirmButtonAction, setStep, setSubmittedInquiryData, setRecommendationCards, setIsRequested, setFinalMarket } =
        React.useContext(CheckoutContext);

    const submitEl = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        setConfirmButtonAction(() => {
            if (submitEl && submitEl.current) {
                submitEl.current.click();
            }
        });
    }, [submitEl]);

    const removeFromList = (id: string | number) => {
        removeItem(id);
    };

    const onSubmit = (values: InquiryFormInterface) => {
        let visitedVenues = null;

        try {
            visitedVenues = localStorage.getItem(StorableDataKeys.VISITED_VENUES);
        } catch (e) {
            console.error('Something wrong with local storage', e);
        }

        let postData: Venues.VenueRecommendationsQueryParams = {
            seed_ids: items.map((item: CartItem) => Number(item.id)),
            count: 6,
        };
        if (visitedVenues) {
            postData.exclude_ids = JSON.parse(visitedVenues);
        }

        const submitData = {
            email: values.email,
            first_name: values.first_name,
            last_name: values.last_name,
            phone_number: values.phone_number,
            guest_count: values.guest_count,
            preferred_date_1: values.date,
            message: values.message,
            contact_reason: null,
            outreach_type: Appointments.OutreachTypes.WEDDING_FLOW,
            tos_shown: false,
            vendor_ids: items.map((item: CartItem) => Number(item.id)) || [],
            creation_source: 'shopping-cart',
        };
        if (!submitData.preferred_date_1) {
            /* @ts-ignore */
            delete submitData.preferred_date_1;
        } else {
            submitData.preferred_date_1 = format(new Date(submitData.preferred_date_1), 'yyyy-MM-dd');
        }

        setIsRequested(true);
        /* @ts-ignore */
        Promise.all([VendorsApi.getVenueRecommendations(postData), VendorsApi.sendMassInquiries(submitData)]).then((resp) => {
            logEvent(AnalyticsEvent.EVTNM_SUCCESSFUL_CART_CHECKOUT, {
                Source: 'Checkout page',
            });
            setRecommendationCards(
                resp[0].map((item: Venues.VenueRecommendation) => ({
                    title: item.name,
                    location: `${item.city}, ${item.state}`,
                    image: item.thumbnail_url,
                    isChecked: true,
                    checkedLabel: 'Send message',
                    id: item.id,
                }))
            );
            setFinalMarket(items[0].market);
            resetCart();
            setStep(Steps.MoreVenues);
            setSubmittedInquiryData(values);
            setIsRequested(false);
        });
    };

    return (
        <div className={styles.inquiryWrapper}>
            <div className={styles.inquiry}>
                <Formik
                    initialValues={{
                        ...initialInquiry,
                        email: user?.email || '',
                        first_name: user?.firstName || '',
                        last_name: user?.lastName || '',
                        phone_number: user?.phoneNumber || '',
                    }}
                    validate={inquiryValidator}
                    onSubmit={onSubmit}
                    enableReinitialize={true}
                >
                    {({ handleSubmit }: FormikProps<InquiryFormInterface>) => (
                        <form onSubmit={handleSubmit}>
                            <p className={styles.venuesCountMobile}>
                                {items.length} venue{items.length !== 1 && 's'} selected
                            </p>
                            <VenuesShoppingCartModalList items={items} onClickRemove={removeFromList} />
                            <Field name='first_name' type='text' label='First Name' component={TextInput} />
                            <Field name='last_name' type='text' label='Last Name' component={TextInput} />
                            <Field name='email' type='email' label='Email' component={TextInput} />
                            <Field name='phone_number' type='text' label='Phone number' component={TextInput} mask='999-999-9999' />
                            <Field name='guest_count' type='number' label='Number of Guests' component={TextInput} />
                            <div className={styles.inquiryDate}>
                                <DatePickerField name='date' label='Wedding date' />
                                <FormikCheckbox name='checkbox' />
                            </div>
                            <div className={styles.textareaWrapper}>
                                <Field
                                    name='message'
                                    type='number'
                                    label='Your message'
                                    placeholder='What do you want to ask this venue?'
                                    component={InputTextarea}
                                />
                            </div>

                            <button type='submit' ref={submitEl}>
                                Submit
                            </button>
                        </form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default InquiryForm;
